import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton } from '@mui/material';
import { useSelector } from 'react-redux';

import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import { useItemAvailability } from 'entities/CatalogItem/lib/useItemAvailability';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { CatalogItemCheckbox } from 'features/CatalogItemCheckbox';
import { useGetItemDataUpdate } from 'features/CatalogItemCheckbox/lib/useGetItemDataUpdate';
import {
    useCatalogIdentity,
    useIsListsAddItemsFlow,
} from 'shared/lib/contexts/CatalogIdentityContext';
import {
    CatalogProductTableRow,
    catalogProductColumnWidths,
} from 'shared/ui/CatalogProductTable';
import { FindReplacementLink } from 'shared/ui/FindReplacementLink/FindReplacementLink';
import { UnavailableTooltipContent } from 'shared/ui/ItemAvailabilityModal';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

import { InventoryMinOnHandCell } from '../InventoryTable/InventoryMinOnHandCell';

interface OrderInventoryTableRowProps {
    item: CatalogItem;
    canSeePrices: boolean;
    onExpandRow: () => void;
}

export const OrderInventoryTableRow = ({
    item,
    canSeePrices,
    onExpandRow,
}: OrderInventoryTableRowProps) => {
    const catalogIdentity = useCatalogIdentity();
    const isListsAddItems = useIsListsAddItemsFlow();
    const itemUid = getItemUid(item, catalogIdentity);
    const { isActive, idToEdit, itemIsInOrder, qty } = useGetItemDataUpdate({
        item,
    });
    const { availabilityStatus, isAvailable } = useItemAvailability(
        item.proposals,
    );
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);

    return (
        <CatalogProductTableRow
            product={{
                title: item.title,
                thumbnailUrl: item.thumbnailUrl,
                categoryId: item.categoryId,
                uom: item.uom,
                proposals: item.proposals,
                price: item.price,
            }}
            dataTestClass="inventory-item-row"
            titleDataTestClass="inventory-item-title"
            showPrice={canSeePrices}
            trailingColumnWidth={catalogProductColumnWidths.orderAdd}
            onClick={() => onExpandRow()}
            quantityCell={
                isListsAddItems ? undefined : (
                    <Box data-testclass="inventory-item-min-on-hand">
                        <InventoryMinOnHandCell
                            minAllowedInStock={item.minAllowedInStock}
                        />
                    </Box>
                )
            }
            trailing={
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '20px',
                    }}
                >
                    {isAvailable ? (
                        <CatalogItemCheckbox
                            showQtyCheckbox
                            isActive={isActive}
                            quickEdit={!isListsAddItems}
                            qty={qty}
                            selectionKey={itemUid}
                            itemUid={itemUid}
                            catalogItemId={item.id}
                            itemIdToEdit={idToEdit}
                            isInOrder={itemIsInOrder}
                            source="inventory"
                            title={item.title}
                            brand={item.brand}
                            thumbnailUrl={item.thumbnailUrl}
                            sku={item.sku}
                        />
                    ) : (
                        <>
                            {!isMaintenanceTechnician && (
                                <FindReplacementLink />
                            )}
                            <DynamicTooltip
                                maxWidth={400}
                                interactive
                                containerStyle={{
                                    display: 'inline-flex',
                                }}
                                content={() => (
                                    <UnavailableTooltipContent
                                        availabilityStatus={availabilityStatus}
                                        hideReplacementLink
                                    />
                                )}
                            >
                                <IconButton
                                    data-testclass="item-suppliers-availability-info-icon"
                                    aria-label="Item availability info"
                                    size="small"
                                    sx={{ padding: 0 }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: '20px',
                                            color: 'error.light',
                                        }}
                                    />
                                </IconButton>
                            </DynamicTooltip>
                        </>
                    )}
                </Box>
            }
        />
    );
};
